import type { AnnotatedJsonSchema } from '@web-config-app/core';

type FormatMessageFn = (descriptor: { id: string }) => string;

const labelFromAnnotation = (
  schemaNode: AnnotatedJsonSchema | undefined,
  formatMessage: FormatMessageFn,
): string | undefined => {
  const xEntityLabel = schemaNode?.['x-entity-label'];
  if (!xEntityLabel) return undefined;

  const { text, key } = xEntityLabel;
  const trimmed = text?.trim();
  if (trimmed) return trimmed;
  if (key) return formatMessage({ id: key });
  return undefined;
};

/**
 * Resolve the user-facing label for a JSON Schema property by reading its
 * `x-entity-label` annotation. A literal `text` wins over a translation
 * `key`; any `key` is resolved through `formatMessage`.
 *
 * For array nodes, the items' `x-entity-label` is preferred when present since it is singular and matches the inline error message prefix..
 *
 * react-intl's `formatMessage` echoes the id verbatim when no translation
 * exists, so `key` always produces a string and this function returns the
 * echoed key in that case rather than `undefined`. That matches how the
 * rest of the FE surfaces missing translations (e.g. breadcrumbs, field
 * labels) and is preferable to falling through to backend-derived
 * placeholder text, which uses BE-internal token names that may not match
 * any FE field naming.
 *
 * Returns `undefined` only when there's no schema node or no
 * `x-entity-label` at all. Callers should still treat `undefined` as
 * "show the placeholder fallback" (or omit the substitution).
 */
export const resolveSchemaPropertyLabel = (
  schemaNode: AnnotatedJsonSchema | undefined,
  formatMessage: FormatMessageFn,
): string | undefined => {
  if (!schemaNode) return undefined;

  if (schemaNode.type === 'array') {
    const { items } = schemaNode;
    const itemsSchema =
      items && !Array.isArray(items)
        ? (items as AnnotatedJsonSchema)
        : undefined;
    const itemLabel = labelFromAnnotation(itemsSchema, formatMessage);
    if (itemLabel) return itemLabel;
  }

  return labelFromAnnotation(schemaNode, formatMessage);
};
